import storeAvatar from "@/assets/figma/card-menu/store-avatar.png";
import storeCountry from "@/assets/figma/card-menu/store-country.png";
import storePlatform from "@/assets/figma/card-menu/store-platform.png";
import { merryCardMenuCatalog } from "@/features/foundation/cardMenuCatalog";
import {
  partitionStoreBindings,
  storeBindingGroupLabels,
  type StoreBindingGroupKey,
} from "@/features/settings/storeManagementGrouping";
import type {
  MerchantStoreBinding,
  MerchantStoreBindingsResponse,
} from "@/features/settings/storeManagementSchemas";
import type {
  MerryCardMenuCatalog,
  MerryCardMenuStore,
} from "@/shared/ui/MerryCardMenu";
import { storeCountryIdentity, storePlatformBadge } from "@/features/settings/storeIdentityVisuals";

const toneByGroup: Readonly<
  Record<StoreBindingGroupKey, MerryCardMenuStore["tone"]>
> = {
  high_risk: "danger",
  pending: "todo",
  stable: "default",
  sync_error: "sync",
};

// 顶部系统菜单沿用 Figma 的高危 → 待办 → 同步 → 稳定视觉顺序。
const systemMenuGroupOrder: ReadonlyArray<StoreBindingGroupKey> = [
  "high_risk",
  "pending",
  "sync_error",
  "stable",
];

export const storeBindingsRetryActionId = "store-bindings-retry";

export type StoreSystemMenuLoadState = "error" | "loading" | "ready";

function statusLabel(binding: MerchantStoreBinding) {
  if (binding.syncStatus !== "healthy") return "同步异常";
  const parts = [];
  if (binding.riskCount > 0) parts.push(`${binding.riskCount}高危`);
  if (binding.pendingTodoCount > 0) {
    parts.push(`${binding.pendingTodoCount}待办`);
  }
  return parts.join(" · ") || "经营平稳";
}

export function createStoreSystemMenuCatalog(
  response?: MerchantStoreBindingsResponse,
  loadState: StoreSystemMenuLoadState = response ? "ready" : "loading",
): MerryCardMenuCatalog {
  const groups = response ? partitionStoreBindings(response.bindings) : null;
  const platformLabels = new Map(
    response?.platforms.map((platform) => [
      platform.provider,
      platform.displayName,
    ]) ?? [],
  );

  return {
    ...merryCardMenuCatalog,
    store: {
      ...merryCardMenuCatalog.store,
      groups: groups
        ? systemMenuGroupOrder.flatMap((groupKey) => {
            const stores = groups[groupKey];
            if (stores.length === 0) return [];
            return [
              {
                id: groupKey,
                label: storeBindingGroupLabels[groupKey],
                stores: stores.map((binding) => {
                  const country = storeCountryIdentity(binding.region);
                  return {
                    avatarSource: storeAvatar,
                    countryCode: country.code,
                    countryLabel: country.label,
                    countrySource: binding.region.trim().toUpperCase() === "US" ? storeCountry : "",
                    id: binding.id,
                    platformBadge: storePlatformBadge(binding.provider),
                    platformLabel: platformLabels.get(binding.provider) ?? binding.provider,
                    platformSource: binding.provider === "tiktok_shop" ? storePlatform : "",
                    provider: binding.provider,
                    statusLabel: statusLabel(binding),
                    title: binding.displayName,
                    tone: toneByGroup[groupKey],
                  };
                }),
              },
            ];
          })
        : [],
      state: loadState === "error"
        ? {
            action: { id: storeBindingsRetryActionId, label: "重新读取店铺" },
            kind: "error",
            message: "店铺列表暂时无法读取",
          }
        : !response
          ? { kind: "loading", message: "正在读取店铺列表…" }
          : response.bindings.length === 0
            ? { kind: "empty", message: "当前还没有绑定店铺，可前往店铺管理完成绑定。" }
            : undefined,
    },
  };
}
